/* ====================================================================
 *  useCatalogQuery — стан фільтрів каталогу, похідний від URL.
 *  REFACTOR_PLAN п.9а.
 *
 *  До 9а стан і URL зв'язували ДВА ЗУСТРІЧНІ watcher'и: один писав стан
 *  в URL, другий читав URL у стан. Це було найкрихкіше місце сторінки —
 *  саме через нього в п.9 я відмовився виносити цей композабл. Тут цикл
 *  не «переписаний акуратніше», а прибраний: джерело правди одне —
 *  `route.query`, стан із нього ОБЧИСЛЮЄТЬСЯ, а зміни йдуть тільки через
 *  router.replace. Кнопка «назад» у браузері працює сама собою, бо
 *  повернення URL автоматично повертає стан.
 * ==================================================================== */

import { computed } from "vue";
import { useRoute, useRouter, type LocationQueryRaw } from "vue-router";
import {
  parseCatalogQuery,
  toCatalogQuery,
  type CatalogFilters,
  type CatalogSort,
} from "~/composables/catalog/catalogFilter";

export function useCatalogQuery() {
  const route = useRoute();
  const router = useRouter();

  const filters = computed<CatalogFilters>(() =>
    parseCatalogQuery(route.query as Record<string, unknown>),
  );

  function apply(patch: Partial<CatalogFilters>) {
    const next = { ...filters.value, ...patch };

    // Чужі параметри (utm_*, gclid тощо) зберігаємо: старий код робив
    // `{...route.query}` і правив лише свої ключі, тож клік по фільтру
    // не збивав атрибуцію переходу. Свої три ключі спершу прибираємо,
    // бо дефолти в URL не пишуться.
    const query: LocationQueryRaw = { ...route.query };
    delete query.cat;
    delete query.sort;
    delete query.ex;
    Object.assign(query, toCatalogQuery(next));

    return router.replace({ path: route.path, query });
  }

  function toggleCategory(slug: string) {
    const set = new Set(filters.value.cats);
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    return apply({ cats: [...set] });
  }

  /** Скидає і категорії, і «Ексклюзив» — як робила кнопка «Усі» до 9а. */
  function clearCategories() {
    return apply({ cats: [], exclusive: false });
  }

  const sort = computed<CatalogSort>({
    get: () => filters.value.sort,
    set: (value) => void apply({ sort: value }),
  });

  const exclusive = computed<boolean>({
    get: () => filters.value.exclusive,
    set: (value) => void apply({ exclusive: value }),
  });

  return { filters, sort, exclusive, apply, toggleCategory, clearCategories };
}
